import {
  baseUrl as defaultBaseUrl,
  doLogout,
  refreshAccessToken
} from "@/api/config";
import { STATUS_CODE } from "@/constants/errorCode";
import {
  getDeepestAdminFilterFirst,
  mapDatasetFiltersToPayload
} from "@/helpers/datasetAttributeHelper";
import { EToastType, showToast } from "@/hooks/ui/useToast";
import type {
  Dataset,
  DatasetChunkStream,
  DatasetData,
  DatasetFilter,
  DatasetFilterPayload,
  DatasetMetadata,
  DatasetMetadataStream,
  H3Configuration
} from "@/types/dataset";
import { buildGridConfig } from "@/utils/dataset";
import {
  DATASET_STREAM_MAX_RETRIES,
  UnauthorizedStreamError
} from "@/utils/errors";
import { generateDatasetData as defaultGenerateDatasetData } from "@/utils/generateDatasetData";

import { fetchEventSource } from "@microsoft/fetch-event-source";

interface IFetchDatasetStreamProps {
  datasetId?: string;
  filters?: DatasetFilter[];
  filterPayload?: DatasetFilterPayload[];
  timezone?: { code: string; name: string };
  h3Configuration?: H3Configuration | null;
  group_by?: string;
  show_id?: boolean;
  page?: number;
  per_page?: number;
  baseUrl?: string;
  signal?: AbortSignal;
  onOpen?: () => void;
  onDatasetMetadata?: (data: DatasetMetadata) => void;
  onError?: (data: unknown) => void;
  generateDatasetData?: (
    data: DatasetChunkStream,
    metadata: DatasetMetadataStream,
    isSpatialAnalysisResult: boolean,
    h3Configuration?: H3Configuration | null
  ) => {
    datasetData: DatasetData;
    newDataset: Dataset | undefined;
  };
}

type StreamResult = Promise<{
  datasetMetadata: DatasetMetadata | undefined;
  rawDataset: DatasetData | undefined;
  dataset: Dataset | undefined;
}>;

class ConnectionStreamError extends Error {
  constructor() {
    super("An error occurred while fetching the dataset stream.");
    this.name = "ConnectionStreamError";
  }
}

function fetchDatasetStreamInternal({
  datasetId,
  filters,
  filterPayload: externalFilterPayload,
  h3Configuration,
  group_by,
  show_id,
  page,
  per_page,
  baseUrl,
  timezone,
  signal: externalSignal,
  generateDatasetData,
  onOpen,
  onError,
  onDatasetMetadata
}: IFetchDatasetStreamProps): StreamResult {
  const transform = generateDatasetData ?? defaultGenerateDatasetData;
  return new Promise((resolve, reject) => {
    let chunkBuffer: DatasetChunkStream["rows"] = [];
    let datasetMetadata: DatasetMetadata | undefined = undefined;
    let datasetMetadataStream: DatasetMetadataStream | undefined = undefined;

    let datasetData:
      | {
          rawDataset: DatasetData;
          dataset: Dataset | undefined;
        }
      | undefined = undefined;

    const filterPayload =
      externalFilterPayload ??
      (() => {
        const filterTransformed = getDeepestAdminFilterFirst(filters ?? []);
        return mapDatasetFiltersToPayload(filterTransformed, timezone);
      })();

    const url = `${baseUrl ?? defaultBaseUrl}/v1/datasets/${datasetId}/data/stream`;
    const controller = new AbortController();
    externalSignal?.addEventListener("abort", () => controller.abort(), {
      once: true
    });

    fetchEventSource(url, {
      method: "POST",
      credentials: "include",
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        filters: filterPayload,
        ...buildGridConfig(h3Configuration),
        group_by,
        show_id,
        page,
        per_page
      }),
      openWhenHidden: true,

      async onopen(response: Response): Promise<void> {
        if (response.ok) {
          onOpen?.();
          return;
        }

        if (response.status === STATUS_CODE.UNAUTHORIZED) {
          throw new UnauthorizedStreamError();
        }

        throw new Error(
          `Unexpected response status: ${response.status.toString()}`
        );
      },

      onmessage(msg) {
        const { event, data } = msg;

        if (!data) return;

        switch (event) {
          case "dataset_detail": {
            datasetMetadata = JSON.parse(data);
            if (datasetMetadata) onDatasetMetadata?.(datasetMetadata);
            break;
          }

          case "dataset_data:meta": {
            datasetMetadataStream = JSON.parse(data);
            break;
          }

          case "dataset_data:chunk": {
            const payload = JSON.parse(data);
            chunkBuffer.push(...payload.rows);
            break;
          }

          case "dataset_data:done": {
            const payload = JSON.parse(data);

            if (datasetMetadata && datasetMetadataStream) {
              const result = transform(
                {
                  dataset_id: payload.dataset_id,
                  rows: chunkBuffer,
                  seq: payload.final_seq
                },
                datasetMetadataStream,
                true,
                h3Configuration
              );

              datasetData = {
                rawDataset: result.datasetData,
                dataset: result.newDataset
              };
            }

            chunkBuffer = [];
            break;
          }

          case "stream:done": {
            if (datasetMetadata && datasetData && datasetMetadataStream) {
              resolve({
                datasetMetadata,
                ...datasetData
              });
            }
            controller.abort();
            break;
          }

          case "stream:error": {
            const err = JSON.parse(data);
            onError?.(err);
            reject(err);
            controller.abort();
            break;
          }
        }
      },

      onerror(err) {
        if (err instanceof UnauthorizedStreamError) {
          // Reject the outer promise, then throw so fetchEventSource calls
          // dispose() and stops retrying. Returning undefined instead would
          // cause a 1-second retry that masks the 401 handling.
          reject(err);
          throw err;
        }

        // Notify the caller of every connection-level failure (not just the
        // final one) so UI can react immediately if needed.
        onError?.(err);

        // Reject with a typed error and throw to stop fetchEventSource from
        // retrying internally. Retry logic with a cap is handled at the
        // fetchDatasetStream wrapper level.
        const connError = new ConnectionStreamError();
        reject(connError);
        throw connError;
      }
    });
  });
}

async function fetchDatasetStreamWithRetries(
  props: IFetchDatasetStreamProps
): StreamResult {
  for (let attempt = 1; attempt <= DATASET_STREAM_MAX_RETRIES; attempt++) {
    try {
      return await fetchDatasetStreamInternal(props);
    } catch (err) {
      if (err instanceof ConnectionStreamError) {
        const isLastAttempt = attempt === DATASET_STREAM_MAX_RETRIES;

        if (!isLastAttempt) continue;

        showToast({
          type: EToastType.ERROR,
          message: "Failed to load dataset. Please try again later."
        });
      }

      // Non-retryable error (UnauthorizedStreamError, stream:error, etc.)
      // or max connection retries exhausted — bubble up
      throw err;
    }
  }

  // Unreachable — TypeScript needs a return path after the loop
  throw new ConnectionStreamError();
}

export async function fetchDatasetStream(
  props: IFetchDatasetStreamProps
): StreamResult {
  try {
    return await fetchDatasetStreamWithRetries(props);
  } catch (err) {
    if (err instanceof UnauthorizedStreamError) {
      await refreshAccessToken();

      try {
        return await fetchDatasetStreamWithRetries(props);
      } catch (retryErr) {
        if (retryErr instanceof UnauthorizedStreamError) {
          await doLogout();
        }
        throw retryErr;
      }
    }
    throw err;
  }
}
